/**
 * Role Service
 * API calls for role management (CRUD + permission linking)
 */

import apiClient from './apiClient';
import {
  Role,
  RoleFormData,
  RoleListResponse,
  RoleResponse,
  RoleFilters,
  PaginationOptions,
} from '../types/rbac.types';

/**
 * Get all roles with filters and pagination
 */
export const getRoles = async (
  filters?: RoleFilters,
  pagination?: PaginationOptions
): Promise<RoleListResponse> => {
  const params = new URLSearchParams();

  // Add filters
  if (filters?.search) params.append('search', filters.search);
  if (filters?.guardName) params.append('guardName', filters.guardName);
  if (filters?.level !== undefined) params.append('level', filters.level.toString());
  if (filters?.department) params.append('department', filters.department);
  if (filters?.roleType) params.append('roleType', filters.roleType);
  if (filters?.isSystemRole !== undefined) params.append('isSystemRole', filters.isSystemRole.toString());
  if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());

  // Add pagination
  if (pagination?.page) params.append('page', pagination.page.toString());
  if (pagination?.limit) params.append('limit', pagination.limit.toString());
  if (pagination?.sortBy) params.append('sortBy', pagination.sortBy);
  if (pagination?.sortOrder) params.append('sortOrder', pagination.sortOrder);

  const response = await apiClient.get<RoleListResponse>(`/roles?${params.toString()}`);
  // httpClient já retorna response.data, então response é diretamente RoleListResponse
  return response as unknown as RoleListResponse;
};

/**
 * Get role by ID
 */
export const getRoleById = async (id: number): Promise<RoleResponse> => {
  const response = await apiClient.get<RoleResponse>(`/roles/${id}`);
  return response as unknown as RoleResponse;
};

/**
 * Create new role
 */
export const createRole = async (data: RoleFormData): Promise<RoleResponse> => {
  const response = await apiClient.post<RoleResponse>('/roles', data);
  return response as unknown as RoleResponse;
};

/**
 * Update role
 */
export const updateRole = async (id: number, data: Partial<RoleFormData>): Promise<RoleResponse> => {
  const response = await apiClient.put<RoleResponse>(`/roles/${id}`, data);
  return response as unknown as RoleResponse;
};

/**
 * Delete role
 */
export const deleteRole = async (id: number): Promise<void> => {
  await apiClient.delete(`/roles/${id}`);
};

/**
 * Sync permissions to role (replace all)
 */
export const syncRolePermissions = async (
  roleId: number,
  permissionIds: number[]
): Promise<RoleResponse> => {
  const response = await apiClient.put<RoleResponse>(`/roles/${roleId}/permissions`, {
    permissionIds,
  });
  return response as unknown as RoleResponse;
};

/**
 * Add permissions to role
 */
export const addRolePermissions = async (
  roleId: number,
  permissionIds: number[]
): Promise<RoleResponse> => {
  const response = await apiClient.post<RoleResponse>(`/roles/${roleId}/permissions`, {
    permissionIds,
  });
  return response as unknown as RoleResponse;
};

/**
 * Remove permissions from role
 */
export const removeRolePermissions = async (
  roleId: number,
  permissionIds: number[]
): Promise<RoleResponse> => {
  const response = await apiClient.delete<RoleResponse>(`/roles/${roleId}/permissions`, {
    data: { permissionIds },
  });
  return response as unknown as RoleResponse;
};

/**
 * Get users with specific role
 */
export const getRoleUsers = async (roleId: number): Promise<any> => {
  const response = await apiClient.get(`/roles/${roleId}/users`);
  return response;
};

/**
 * Get all active roles (for dropdowns)
 */
export const getActiveRoles = async (): Promise<{ message: string; data: Role[] }> => {
  const response = await apiClient.get<{ message: string; data: Role[] }>('/roles/active');
  return response as unknown as { message: string; data: Role[] };
};

export default {
  getRoles,
  getRoleById,
  createRole,
  updateRole,
  deleteRole,
  syncRolePermissions,
  addRolePermissions,
  removeRolePermissions,
  getRoleUsers,
  getActiveRoles,
};
